import { CorporateAdmins, CorporateClients, Users } from '@platform/client';
import type { CohortType } from '@platform/client/schemas';
import { PillBadge, type PillBadgeColors } from '@platform/components';
import { useEffect, useMemo } from 'react';
import { useLocation, useSearch } from 'wouter';
import { navigate } from 'wouter/use-browser-location';
import { AuthedPage, HeaderFooterLayout } from '../app';
import { formatDateStr } from '../dashboard/my-projects/utils';
import { ROUTES } from '../routes/consts';
import { ApplicantsTable } from './dashboard/ApplicantsTable';
import { CohortsDropDown, sortCohortsForDisplay } from './dashboard/CohortsDropDown';
import { CohortStatsBox } from './dashboard/CohortStatsBox';

const COHORT_TYPE_PILL_COLOR: Record<CohortType, PillBadgeColors> = {
  Teams: 'blue',
  Individual: 'success-25',
  'Individual - Assigned': 'secondary-brand-50',
  'Self-Paced - Individual': 'orange',
};

const CORPORATE_CLIENT_DASHBOARD_TITLE: Record<number, string> = {
  1: 'SAP Acceleration Collective Dashboard',
  2: 'EY Pro Bono Connect Dashboard',
  3: 'Lumen Volunteer Engagement Dashboard',
  4: 'F5 Community Impact Dashboard',
};

export const CorporateDashboard = () => {
  const { data: user, isSuccess, isLoading, isError } = Users.useGetCurrentUser();
  const search = useSearch();
  const [, setLocation] = useLocation();
  const params = new URLSearchParams(search);
  const cohortIdParam = params.get('cohortId');
  const corporateClientIdParam = params.get('corporateClientId');
  const adminCorporateClientId = corporateClientIdParam ? Number(corporateClientIdParam) : undefined;
  const selectedCohortId = cohortIdParam ? Number(cohortIdParam) : undefined;

  const isCorporateAdmin = user?.accountType === 'corporate';
  // Platform admins view any client's dashboard by passing ?corporateClientId in the URL.
  const isPlatformAdmin = user?.role === 'admin';
  const canViewDashboard =
    isCorporateAdmin || (isPlatformAdmin && adminCorporateClientId !== undefined);

  const { data: cohortsData } = CorporateAdmins.useGetClientCohorts(
    isPlatformAdmin ? { corporate_client_id: adminCorporateClientId } : undefined,
    { query: { enabled: canViewDashboard } },
  );
  // Only needed to resolve the dashboard title when an admin is viewing another client.
  const { data: corporateClients } = CorporateClients.useGetAllCorporateClients({
    query: { enabled: isPlatformAdmin },
  });

  const setSelectedCohortId = (id: number, options?: { replace?: boolean }) => {
    const next = new URLSearchParams(search);
    next.set('cohortId', String(id));
    setLocation(`${ROUTES.CORPORATE_DASHBOARD}?${next.toString()}`, { replace: options?.replace });
  };
  const sortedCohorts = useMemo(
    () => sortCohortsForDisplay(cohortsData?.cohorts ?? []),
    [cohortsData],
  );
  const selectedCohort = sortedCohorts.find((c) => c.id === selectedCohortId);

  const userCorporateClientId =
    user && 'corporateClientId' in user ? user.corporateClientId : undefined;
  const userCorporateClientName =
    user && 'corporateClientName' in user ? user.corporateClientName : undefined;
  const effectiveClientId = isPlatformAdmin ? adminCorporateClientId : userCorporateClientId;
  const dashboardTitle =
    (effectiveClientId && CORPORATE_CLIENT_DASHBOARD_TITLE[effectiveClientId]) ??
    `${
      corporateClients?.find((c) => c.id === effectiveClientId)?.name ??
      userCorporateClientName ??
      'Corporate'
    } Dashboard`;

  useEffect(() => {
    if (isSuccess && !canViewDashboard) {
      navigate('/');
    }
  }, [isSuccess, canViewDashboard]);

  useEffect(() => {
    if (!canViewDashboard) return;
    if (cohortIdParam) return;
    if (sortedCohorts.length === 0) return;
    // Replace (don't push) so auto-selecting the default cohort doesn't add a history entry — otherwise
    // the browser back button lands on the cohort-less URL and this effect bounces forward again.
    setSelectedCohortId(sortedCohorts[0].id, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewDashboard, cohortIdParam, sortedCohorts]);

  return (
    <AuthedPage>
      <HeaderFooterLayout>
        <div className='w-full px-6 md:px-10 lg:px-16 pb-10 bg-gray-50'>
          {isError && <div>Error loading user data</div>}
          {isLoading && <div>Loading...</div>}
          {isSuccess && canViewDashboard && (
            <>
              <h1 className='text-4xl font-medium tracking-tight text-gray-600'>
                {dashboardTitle}
              </h1>

              <div className='mt-6 flex flex-col gap-2'>
                <span className='text-base font-medium text-gray-700'>Cohort</span>
                <div className='flex items-center gap-3'>
                  <CohortsDropDown
                    selectedCohortId={selectedCohortId}
                    onCohortSelect={setSelectedCohortId}
                    corporateClientId={isPlatformAdmin ? adminCorporateClientId : undefined}
                  />
                  {selectedCohort && selectedCohort.type && (
                    <PillBadge
                      variant='plain'
                      size='md'
                      color={COHORT_TYPE_PILL_COLOR[selectedCohort.type]}
                    >
                      {selectedCohort.type}
                    </PillBadge>
                  )}
                </div>
              </div>

              {selectedCohort && (
                <div className='mt-4 flex flex-wrap gap-x-8 gap-y-2 text-md'>
                  <div>
                    <span className='font-medium text-gray-500'>Application Period: </span>
                    <span className='font-semibold text-gray-700'>
                      {selectedCohort.applicationStart ?
                        formatDateStr(selectedCohort.applicationStart)
                      : '—'}
                      {' - '}
                      {selectedCohort.applicationEnd ?
                        formatDateStr(selectedCohort.applicationEnd)
                      : '—'}
                    </span>
                  </div>
                  <div>
                    <span className='font-medium text-gray-500'>Cohort Dates: </span>
                    <span className='font-semibold text-gray-700'>
                      {selectedCohort.startDate ? formatDateStr(selectedCohort.startDate) : '—'}
                      {' - '}
                      {selectedCohort.endDate ? formatDateStr(selectedCohort.endDate) : '—'}
                    </span>
                  </div>
                </div>
              )}

              {selectedCohortId && selectedCohort && (
                <>
                  <div className='mt-8'>
                    <CohortStatsBox cohortId={selectedCohortId} cohort={selectedCohort} />
                  </div>
                  <div className='mt-8'>
                    <ApplicantsTable cohortId={selectedCohortId} cohort={selectedCohort} />
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </HeaderFooterLayout>
    </AuthedPage>
  );
};
